import type { ChatModelRunResult } from '@assistant-ui/react';
import { ticketFormPath } from '../lib/assistantLinks';
import { parseConfirmationDetails } from '../lib/assistantConfirmation';
import type { AssistantEvent, AssistantToolStatus, ConfirmationDetail } from '../types/assistant';

/**
 * State of one assistant answer while its stream is read.
 *
 * assistant-ui's streaming contract is CUMULATIVE: every `yield` REPLACES the
 * message content, so a growing text buffer and the list of tool steps are
 * kept here and the whole content is re-emitted after each frame.
 */
export interface ToolStep {
  toolCallId: string;
  toolName: string;
  /** Ready German label from the server; the tool name when it sent none. */
  label: string;
  running: boolean;
  status?: AssistantToolStatus;
  summary?: string;
  /** `needs_confirmation` only: exactly what will be written. */
  details?: ConfirmationDetail[];
  /** `needs_confirmation` only: kept in memory with the step, see `assistantConfirmation`. */
  confirmToken?: string;
}

export interface StreamState {
  text: string;
  tools: ToolStep[];
}

type Content = NonNullable<ChatModelRunResult['content']>;

export const initialStreamState = (): StreamState => ({ text: '', tools: [] });

/** The way out when the assistant cannot help: the ticket form, conversation attached. */
export function ticketEscapeLink(conversationId?: number | null): string {
  return `[Ticket direkt schreiben](${ticketFormPath(conversationId)})`;
}

const appendParagraph = (text: string, addition: string): string =>
  text ? `${text}\n\n${addition}` : addition;

/** Folds one server event into the state. Returns a new state, never mutates. */
export function applyEvent(
  state: StreamState,
  event: AssistantEvent,
  conversationId?: number | null,
): StreamState {
  switch (event.type) {
    case 'token':
      return { ...state, text: state.text + event.text };
    case 'tool_start':
      return {
        ...state,
        tools: [
          ...state.tools,
          {
            toolCallId: `t${state.tools.length}`,
            toolName: event.tool,
            label: event.label?.trim() || event.tool,
            running: true,
          },
        ],
      };
    case 'tool_result': {
      // Close the most recent step of this tool that is still running.
      let index = -1;
      state.tools.forEach((step, i) => {
        if (step.toolName === event.tool && step.running) index = i;
      });
      if (index === -1) return state;
      const status = event.status ?? 'ok';
      const waiting = status === 'needs_confirmation';
      const details = waiting ? parseConfirmationDetails(event.details) : [];
      const tools = state.tools.map((step, i) =>
        i === index
          ? {
              ...step,
              running: false,
              status,
              summary: event.summary,
              ...(details.length > 0 ? { details } : {}),
              // A token only means something on a step that waits for it.
              ...(waiting && typeof event.confirm_token === 'string' && event.confirm_token !== ''
                ? { confirmToken: event.confirm_token }
                : {}),
            }
          : step,
      );
      return { ...state, tools };
    }
    case 'done':
      // The final reply is the source of truth for the text.
      return event.reply ? { ...state, text: event.reply } : state;
    case 'error':
      return {
        ...state,
        text: appendParagraph(appendParagraph(state.text, event.message), ticketEscapeLink(conversationId)),
      };
  }
}

/** The cumulative content array assistant-ui expects for one yield. */
export function buildContent(state: StreamState): Content {
  const parts: Content[number][] = state.tools.map((step) => ({
    type: 'tool-call' as const,
    toolCallId: step.toolCallId,
    toolName: step.toolName,
    args: {},
    argsText: '{}',
    // The chip's German text travels as a UI-only artifact.
    artifact: { label: step.label },
    // Presence of `result` flips the tool UI from "running" to "complete".
    ...(step.running
      ? {}
      : {
          result: {
            status: step.status ?? 'ok',
            ...(step.summary ? { summary: step.summary } : {}),
            ...(step.details ? { details: step.details } : {}),
            ...(step.confirmToken ? { confirmToken: step.confirmToken } : {}),
          },
        }),
  }));
  if (state.text) parts.push({ type: 'text' as const, text: state.text });
  return parts;
}

/** One `data: {json}` line of a frame, or null when it is none or malformed. */
function parseDataLine(line: string): AssistantEvent | null {
  const trimmed = line.trimStart();
  if (!trimmed.startsWith('data:')) return null;
  const payload = trimmed.slice(5).trim();
  if (!payload) return null;
  try {
    const event: unknown = JSON.parse(payload);
    return typeof event === 'object' && event !== null && typeof (event as { type?: unknown }).type === 'string'
      ? (event as AssistantEvent)
      : null;
  } catch {
    // Ignore malformed frames rather than aborting the whole stream.
    return null;
  }
}

/**
 * Reads a server-sent-event body and yields the events of each complete frame
 * (frames are separated by a blank line) as one batch, so the caller yields
 * one snapshot per frame.
 */
export async function* readEventFrames(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<AssistantEvent[], void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let separator = buffer.indexOf('\n\n');
    while (separator !== -1) {
      const frame = buffer.slice(0, separator);
      buffer = buffer.slice(separator + 2);
      const events = frame
        .split('\n')
        .map(parseDataLine)
        .filter((event): event is AssistantEvent => event !== null);
      yield events;
      separator = buffer.indexOf('\n\n');
    }
  }
  // A last frame without its trailing blank line still counts.
  const rest = buffer
    .split('\n')
    .map(parseDataLine)
    .filter((event): event is AssistantEvent => event !== null);
  if (rest.length > 0) yield rest;
}
